import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import { appBackgroundColor } from "../../theme";
import { uiNeutrals } from "../../constants/uiNeutrals";

const getShimmerStyle = () => ({
  background:
    "linear-gradient(90deg, rgba(236,234,232,0.95) 25%, rgba(220,216,212,0.98) 50%, rgba(236,234,232,0.95) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite",
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "-200% 0" },
    "100%": { backgroundPosition: "200% 0" },
  },
});

const DealDetailsSkeleton = ({ onClose }) => {
  const safeTop =
    "calc(constant(safe-area-inset-top) + env(safe-area-inset-top))";
  const safeBottom =
    "calc(constant(safe-area-inset-bottom) + env(safe-area-inset-bottom))";

  const headerBarPx = 56;
  const footerBarPx = 52;

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        bgcolor: appBackgroundColor,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          boxSizing: "border-box",
          bgcolor: "rgba(253, 252, 251, 0.88)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          pt: safeTop,
          borderBottom: `1px solid ${uiNeutrals.rowDivider}`,
        }}
      >
        <Box
          sx={{
            height: `${headerBarPx}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: 20,
              maxWidth: 200,
              borderRadius: 1,
              ...getShimmerStyle(),
            }}
          />
          <IconButton
            onClick={onClose}
            disabled={!onClose}
            aria-label="Close"
            edge="end"
            sx={{
              ...closeIconButtonSx,
              ...(onClose ? {} : { opacity: 0.5 }),
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "260px",
            aspectRatio: "1/1",
            mt: 2,
            mx: "auto",
            ...getShimmerStyle(),
          }}
        />

        <Box sx={{ px: 2.5, pt: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: "90%",
              height: 32,
              borderRadius: 1,
              mb: 1,
              ...getShimmerStyle(),
            }}
          />
          <Box
            sx={{
              width: 100,
              height: 14,
              borderRadius: 1,
              ...getShimmerStyle(),
            }}
          />
        </Box>

        <Box sx={{ px: 2.5, mb: 1, display: "flex", gap: 1 }}>
          <Box
            sx={{
              width: 80,
              height: 28,
              borderRadius: "20px",
              ...getShimmerStyle(),
            }}
          />
          <Box
            sx={{
              width: 80,
              height: 28,
              borderRadius: "20px",
              ...getShimmerStyle(),
            }}
          />
        </Box>

        <Box sx={{ px: 2.5 }}>
          {[1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5,
                mb: i < 4 ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  ...getShimmerStyle(),
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    width: "40%",
                    height: 16,
                    borderRadius: 1,
                    mb: 1,
                    ...getShimmerStyle(),
                  }}
                />
                <Box
                  sx={{
                    width: "80%",
                    height: 16,
                    borderRadius: 1,
                    ...getShimmerStyle(),
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          boxSizing: "border-box",
          bgcolor: "rgba(253, 252, 251, 0.92)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          pb: safeBottom,
          borderTop: `1px solid ${uiNeutrals.rowDivider}`,
        }}
      >
        <Box
          sx={{
            height: `${footerBarPx}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 28,
              borderRadius: "9999px",
              ...getShimmerStyle(),
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                ...getShimmerStyle(),
              }}
            />
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                ...getShimmerStyle(),
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DealDetailsSkeleton;
